import {useCustomPreferences} from '../context/preferences'
import {useLocaleCountry} from './use-locale-custom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * Retrieves a specific custom preference value based on the provided key and locale.
 *
 * @param {Array} preferences - The array of custom preferences.
 * @param {string} key - The key of the custom preference to retrieve.
 * @param {string} locale - The current locale.
 * @returns {any} - The value of the custom preference for the given locale, or null if not found.
 */
const getPreferenceValue = (preferences, key, locale) => {
    const preference = preferences.find((pref) => pref.id === key)
    try {
        return preference ? JSON.parse(preference.value)?.[locale] ?? null : null
    } catch (error) {
        console.error('getPreferenceValue - Malformed JSON', key, locale, error)
        return null
    }
}

const getMarketlessPreferenceValue = (preferences, key) => {
    const preference = preferences.find((pref) => pref.id === key)
    try {
        return preference ? JSON.parse(preference.value) : null
    } catch (error) {
        console.error('getMarketlessPreferenceValue - Malformed JSON', key, error)
        return null
    }
}

const useCustomPreference = (key) => {
    const customPreferences = useCustomPreferences()
    const currentCountry = useLocaleCountry().toLowerCase()
    return getPreferenceValue(customPreferences, key, currentCountry)
}

const useBooleanCustomPreference = (key) => {
    const value = useCustomPreference(key)
    return value === 'true' ? true : value === 'false' ? false : null
}

const useCustomPreferenceWithLocale = (key) => {
    const customPreferences = useCustomPreferences()
    const {locale} = useMultiSite()
    return getPreferenceValue(customPreferences, key, locale.alias)
}

const useCustomPreferenceMarketless = (key) => {
    const customPreferences = useCustomPreferences()
    return getMarketlessPreferenceValue(customPreferences, key)
}

export {
    useCustomPreference,
    useBooleanCustomPreference,
    useCustomPreferenceWithLocale,
    useCustomPreferenceMarketless,
}
